import { CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Skeleton, Space, Tag, Typography, theme } from 'antd';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchIndustrialRefBookItem } from '@/api/endpoints/industrialRefBook';
import {
  INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT,
  INDUSTRIAL_REF_BOOK_COLOR_LOCATION,
  INDUSTRIAL_REF_BOOK_COLOR_TERMINAL,
} from '@/components/map/industrialRefBookLayer';
import type { IndustrialRefBookRecord } from '@/types/industrialRefBook';

const CARD_WIDTH_PX = 320;
const LAT_LON_DECIMALS = 5;

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly record: IndustrialRefBookRecord }
  | { readonly status: 'error'; readonly message: string };

interface IndustrialRefBookCardProps {
  readonly itemId: number;
  readonly onUseAsOrigin: (record: IndustrialRefBookRecord) => void;
  readonly onUseAsDestination: (record: IndustrialRefBookRecord) => void;
  readonly onClose: () => void;
}

export default function IndustrialRefBookCard({
  itemId,
  onUseAsOrigin,
  onUseAsDestination,
  onClose,
}: IndustrialRefBookCardProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchIndustrialRefBookItem(itemId)
      .then((record) => {
        if (!cancelled) setState({ status: 'success', record });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'unknown error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const containerStyle: CSSProperties = {
    position: 'absolute',
    top: 16,
    // Sit to the right of the map so it doesn't fight the legend on the
    // left. 16px gutter from the right edge.
    right: 16,
    width: CARD_WIDTH_PX,
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    boxShadow: token.boxShadow,
    zIndex: 410,
    pointerEvents: 'auto',
  };

  return (
    <div style={containerStyle}>
      <Card
        size="small"
        styles={{ body: { padding: 12 } }}
        style={{ background: 'transparent', border: 'none' }}
        extra={
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label={t('industrialRefBook.card.close')}
          />
        }
        title={
          <Typography.Text strong style={{ fontSize: 13 }}>
            {t('industrialRefBook.card.title')}
          </Typography.Text>
        }
      >
        {state.status === 'loading' && <Skeleton active paragraph={{ rows: 2 }} />}
        {state.status === 'error' && (
          <Alert type="error" showIcon message={state.message} />
        )}
        {state.status === 'success' && (
          <CardBody
            record={state.record}
            onUseAsOrigin={() => onUseAsOrigin(state.record)}
            onUseAsDestination={() => onUseAsDestination(state.record)}
          />
        )}
      </Card>
    </div>
  );
}

interface CardBodyProps {
  readonly record: IndustrialRefBookRecord;
  readonly onUseAsOrigin: () => void;
  readonly onUseAsDestination: () => void;
}

function CardBody({ record, onUseAsOrigin, onUseAsDestination }: CardBodyProps) {
  const { t } = useTranslation();
  return (
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      <Space size={4} style={{ width: '100%', flexWrap: 'wrap' }}>
        <Tag color={typeColor(record.type)} style={{ margin: 0 }}>
          {t(`industrialRefBook.type.${record.type}`, { defaultValue: record.type })}
        </Tag>
        {record.is_company && (
          <Tag style={{ margin: 0 }}>{t('industrialRefBook.card.company')}</Tag>
        )}
        {record.year !== null && (
          <Tag style={{ margin: 0 }}>{record.year}</Tag>
        )}
      </Space>
      <Typography.Text strong style={{ fontSize: 13 }}>
        {record.title}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {record.address}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        <EnvironmentOutlined />{' '}
        {record.latitude.toFixed(LAT_LON_DECIMALS)},{' '}
        {record.longitude.toFixed(LAT_LON_DECIMALS)}
      </Typography.Text>
      <Space size={4} style={{ width: '100%' }}>
        <Button size="small" onClick={onUseAsOrigin} block>
          {t('industrialRefBook.card.useAsOrigin')}
        </Button>
        <Button size="small" type="primary" onClick={onUseAsDestination} block>
          {t('industrialRefBook.card.useAsDestination')}
        </Button>
      </Space>
    </Space>
  );
}

function typeColor(type: string): string | undefined {
  switch (type) {
    case 'LOCATION':
      return INDUSTRIAL_REF_BOOK_COLOR_LOCATION;
    case 'AGRO_POINT':
      return INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT;
    case 'TERMINAL':
      return INDUSTRIAL_REF_BOOK_COLOR_TERMINAL;
    default:
      return undefined;
  }
}
